
export const formatCompact = (value: number | string, full?: boolean): string => {
  if (typeof value === 'string') return value
  if (value < 1) return value.toFixed(2).replace(/\.?0+$/, '')
  if (value < 10) return value.toFixed(1).replace(/\.0$/, '')
  if (value < 1000) return Math.round(value).toString()
  const suffixes = ['', 'k', 'M', 'B', 'T']
  const longSuffixes = ['', ' thousand', ' million', ' billion', ' trillion', ' quadrillion', ' quintillion']
  const tier = Math.floor(Math.log10(value) / 3)
  const suffix = !full ?suffixes[Math.min(tier, suffixes.length - 1)] : longSuffixes[tier]
  const scale = Math.pow(10, tier * 3)
  const scaled = value / scale
  if (scaled >= 100) return `${Math.round(scaled)}${suffix}`
  if (scaled >= 10) return `${scaled.toFixed(1).replace(/\.0$/, '')}${suffix}`
  return `${scaled.toFixed(2).replace(/\.?0+$/, '')}${suffix}`
}

// Same as `formatCompact` but with an uppercase "K" for thousands, matching
// the BMTQ casing convention some charts use ("220K", "3.5B").
export const formatCompactUpper = (value: number | string): string =>
  formatCompact(value).replace('k', 'K')

// Split a compact-formatted string into its numeric mantissa and magnitude
// suffix (K/M/B/T/Q). Returns null suffix when the value is under 1000.
// Used to render the suffix in a dimmed style.
export const splitCompactMantissaSuffix = (compact: string): { mantissa: string, suffix: string | null } => {
  const match = compact.match(/^(-?[\d.]+)([kKMBTQ])?$/)
  if (!match) return { mantissa: compact, suffix: null }
  return { mantissa: match[1], suffix: match[2] ?? null }
}


// ============ MAGNITUDE BOX CONFIGS PER RESOURCE ============
// Each level specifies: { size: pixel size, value: how many units this box represents }
export type BoxLevelConfig = { size: number; value: number }
export type ResourceType = 'robots' | 'energy' | 'default' | 'compute' | 'agents' | 'labs' | 'researchers'

const DEFAULT_BOX_CONFIGS: Record<ResourceType, BoxLevelConfig[]> = {
  robots: [
    { size: 4, value: 1e4 },     // level 0: each small box = 10k robots
    { size: 8, value: 1e7 },     // level 0: each small box = 1M robots
    { size: 19, value: 1e9 },     // level 1: each medium box = 100M robots
    { size: 34, value: 3e10 },   // level 2: each large box = 30B robots
    { size: 50, value: 1.4e13 },   // level 3: each XL box = 1T robots
  ],
  energy: [
    { size: 8, value: 0.05 },     // level 0: each small box = 0.1 Terawatts
    { size: 19, value: 1},      // level 1: each medium box = 10 Terawatts
    { size: 34, value: 20 },    // level 2: each large box = 100 Terawatts
    { size: 50, value: 400 },   // level 3: each XL box = 1000 Terawatts
  ],
  compute: [
    { size: 8, value: 1e6 },     // level 0: each small box = 1M robots
    { size: 21, value: 1e8 },     // level 1: each medium box = 100M robots
    { size: 37, value: 1e11 },   // level 2: each large box = 30B robots
    { size: 72, value: 1e14 },   // level 3: each XL box = 1T robots
  ],
  agents: [
    { size: 10, value: 1000 },
    { size: 21, value: 10000 },
    { size: 32, value: 100000 },
    { size: 48.5, value: 1000000 },
  ],
  labs: [
    { size: 5, value: 1 },
    { size: 11, value: 100 },
    { size: 21, value: 10000 },
    { size: 33, value: 1000000 },
  ],
  researchers: [
    { size: 10, value: 1000 },
    { size: 21, value: 10000 },
    { size: 32, value: 100000 },
    { size: 48.5, value: 1000000 },
  ],
  default: [
    { size: 4, value: 1 },
    { size: 9, value: 10 },
    { size: 19, value: 100 },
    { size: 39, value: 1000 },
  ],
}
const BOX_MARGIN = 1
// ==========================================================

export const parseEnergy = (energyStr: string): number => {
  if (!energyStr || energyStr === '—') return 0
  const match = energyStr.match(/^([\d.]+)(GW|TW)$/)
  if (!match) return 0
  const value = parseFloat(match[1])
  const unit = match[2]
  return unit === 'TW' ? value : value / 1000
}

const  getConfig = (resource: ResourceType, customConfigs?: Record<ResourceType, BoxLevelConfig[]>): BoxLevelConfig[] => {
  const configs = customConfigs || DEFAULT_BOX_CONFIGS
  return configs[resource] || configs.default
}

export const getBoxSize = (level: number, resource: ResourceType = 'default', customConfigs?: Record<ResourceType, BoxLevelConfig[]>) => {
  if (customConfigs) {
    const config = getConfig(resource, customConfigs)
    if (level < config.length) return config[level].size
    return config[config.length - 1].size
  }
  if (level == 0) return 8
  if (level == 1) return 17
  if (level == 2) return 35
  if (level == 3) return 71
}

export const getValueForLevel = (level: number, resource: ResourceType = 'default', customConfigs?: Record<ResourceType, BoxLevelConfig[]>) => {
  const config = getConfig(resource, customConfigs)
  if (level < config.length) return config[level].value
  // Extrapolate: each level beyond config is 10x the last
  return config[config.length - 1].value * Math.pow(10, level - config.length + 1)
}

export const BOX_MARGIN_CONST = BOX_MARGIN
